// eslint-disable-next-line custom-config/prevent-chakra-import
import { Box, BoxProps, forwardRef } from '@chakra-ui/react'
import { useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'

export type IconProps = BoxProps

type IconPath = string | { d: string; opacity?: number }

type CreateIconOptions = {
  displayName: string
  d?: string
  paths?: IconPath[]
  sx?: BoxProps['sx']
  /** Canvas (Sana) replacement paths, rendered instead of `d`/`paths` when `sana_rebrand_enabled` is on. */
  canvasPaths?: IconPath[]
  /**
   * Overrides the rendered viewBox regardless of `sana_rebrand_enabled`. For icons sourced
   * directly from a Canvas icon with no legacy equivalent — those are always in Canvas's
   * 24x24 coordinate space, flag or no flag, so pass `'0 0 24 24'` here instead of `canvasPaths`.
   */
  viewBox?: string
}

const SANA_VIEW_BOX = '0 0 24 24'

/**
 * If you are confused about the viewBox being hard-coded (I was) it's because you need to
 *  import the icons, please read this: https://confluence.workday.com/display/VNDLY/VNDLY+Icons
 */
export const Icon = forwardRef<BoxProps & { viewBox?: string }, 'div'>((props, ref) => {
  return (
    <Box
      ref={ref}
      as="svg"
      viewBox="0 0 1024 1024"
      flexShrink={0}
      display="inline-block"
      verticalAlign="middle"
      role="img"
      w="24px"
      h="24px"
      fill="currentColor"
      {...props}
    />
  )
})

// jest-preview is busted and renders icons WAY TOO LARGE, so in tests lets hard code them to a 16px size

/**
 * If you are confused about the viewBox being hard-coded (I was) it's because you need to
 *  import the icons, please read this: https://confluence.workday.com/display/VNDLY/VNDLY+Icons
 */
export const createIcon = ({ displayName, d, paths, canvasPaths, sx, viewBox, ...rest }: CreateIconOptions) => {
  const Comp = forwardRef<IconProps, 'div'>((props, ref) => {
    const isSanaRebrandEnabled = useIsSanaRebrandEnabled()
    const shouldUseSanaPaths = isSanaRebrandEnabled && Boolean(canvasPaths?.length)
    const activeD = shouldUseSanaPaths ? undefined : d
    const activePaths = shouldUseSanaPaths ? canvasPaths : paths
    const activeViewBox = viewBox ?? (shouldUseSanaPaths ? SANA_VIEW_BOX : undefined)

    return (
      <Icon
        aria-hidden
        ref={ref}
        {...(activeViewBox ? { viewBox: activeViewBox } : {})}
        {...props}
        {...rest}
        sx={{ ...sx, ...props.sx }}>
        {activeD && <path d={activeD} />}
        {activePaths &&
          activePaths.map(path => {
            const { d: pathD, opacity } = typeof path === 'string' ? { d: path, opacity: undefined } : path
            return <path d={pathD} opacity={opacity} key={pathD} />
          })}
      </Icon>
    )
  })

  if (process.env.NODE_ENV !== 'production') {
    Comp.displayName = displayName
  }

  return Comp
}
